import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useOnUnmount } from '~/_shared/hooks/useOnUnmount';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useModal } from '~/modal/useModal.hook';
import { SidebarApp } from '~/sidebar/sidebarApp.enum';
import { useCreateOptimizedTerritoriesWithSubscriptionCheck } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/useCreateBoundaryGroupFromAIWithSubscriptionCheck';
import {
  clearActiveMapTemplate, openLeftSidebarApp, openMapTool,
} from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  useActiveMapTemplateSelectionIdSelector, useActiveMapTemplateSelector,
} from '~/store/frontendState/leftSidebar/leftSidebar.selectors';
import { useGetCurrentPageInfo } from '~/topBar/navigation/navigation.helpers';
import { MapListSortType } from '../listing/mapListing.helpers';
import { useMapListingItems } from '../listing/useMapListingItems';
import {
  getTemplateToolOrApp, MapTemplates,
} from './mapTemplates.helpers';

export const useOpenMapTemplates = () => {
  const dispatch = useDispatch();
  const activeMapTemplate = useActiveMapTemplateSelector();
  const activeMapTemplateSelectionId = useActiveMapTemplateSelectionIdSelector();
  const currentPage = useGetCurrentPageInfo().page;
  const { openOptimizedTerritoriesModal } = useCreateOptimizedTerritoriesWithSubscriptionCheck();
  const { openModal: openLocationFinderModal } = useModal('LocationFinder');
  const { openModal: openFilterSettingsModal } = useModal('FilterSettings');
  const { openModal: openLayeringModal } = useModal('CreateLayeredMap');

  const { mapListingItems, isLoading: isMapListingLoading } = useMapListingItems({
    isActive: activeMapTemplate === MapTemplates.Layering,
    searchQuery: '',
    sortType: MapListSortType.Created_DESC,
  });

  useEffect(() => {
    if (!activeMapTemplate) {
      return;
    }

    switch (activeMapTemplate) {
      case MapTemplates.LocationFind:
        openLocationFinderModal({});
        dispatch(clearActiveMapTemplate());
        break;
      case MapTemplates.ATB:
        openOptimizedTerritoriesModal();
        dispatch(clearActiveMapTemplate());
        break;
      case MapTemplates.Filter:
        openFilterSettingsModal();
        dispatch(clearActiveMapTemplate());
        break;
      case MapTemplates.Styling:
        dispatch(openLeftSidebarApp(SidebarApp.MapSettings, currentPage));
        break;
      case MapTemplates.Layering:
        if (!isMapListingLoading && mapListingItems.length >= 2) {
          dispatch(clearActiveMapTemplate());
          openLayeringModal();
        }
        break;
      default:
        break;
    }

    const mapTool = getTemplateToolOrApp(activeMapTemplate)?.mapTool;
    if (notNullsy(mapTool)) {
      dispatch(openMapTool(mapTool));
    }
  }, [activeMapTemplate, activeMapTemplateSelectionId, currentPage, dispatch, isMapListingLoading, mapListingItems.length,
    openOptimizedTerritoriesModal, openFilterSettingsModal, openLayeringModal, openLocationFinderModal,
  ]);

  useOnUnmount(() => {
    dispatch(clearActiveMapTemplate());
  });
};
